import { useEffect, useState } from 'react';
import * as React from 'react';
import { mergeProps as mergePropsReactAria } from './mergeProps';
import { log } from '@livekit/components-core';
import clsx from 'clsx';
import { apiFetch } from './ultra-meet-config';

export function useServerUrl(region?: string, options: { enabled?: boolean } = {}) {
    const [serverUrl, setServerUrl] = useState<string | undefined>();
    useEffect(() => {
        let cancelled = false;

        if (options.enabled === false) {
            setServerUrl(undefined);
            return;
        }

        const loadUrl = async () => {
            let endpoint = `/api/url`;
            if (region) {
                endpoint += `?region=${region}`;
            }
            try {
                const res = await apiFetch(endpoint);
                if (!res.ok) {
                    log.error('Error fetching server url', { status: res.status, statusText: res.statusText });
                    return;
                }
                const body = await res.json();
                if (!cancelled) {
                    setServerUrl(body.url);
                }
            } catch (error) {
                log.error('Error fetching server url', { error });
            }
        };

        loadUrl();

        return () => {
            cancelled = true;
        };
    }, [region, options.enabled]);
    return serverUrl;
}

export function generateRoomId(projectId: string): string {
    return `${projectId}-${randomString(4)}-${randomString(4)}`;
}

export function randomString(length: number): string {
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const randomValues = new Uint8Array(length);

    // Use Web Crypto API for cryptographically secure randomness.
    if (globalThis.crypto?.getRandomValues) {
        globalThis.crypto.getRandomValues(randomValues);
    } else {
        for (let index = 0; index < randomValues.length; index += 1) {
            randomValues[index] = Math.floor(Math.random() * 256);
        }
    }

    let result = '';
    for (let i = 0; i < length; i++) {
        result += characters.charAt(randomValues[i] % characters.length);
    }
    return result;
}

/** @internal */
export function isProp<U extends HTMLElement, T extends React.HTMLAttributes<U>>(prop: T | undefined): prop is T {
    return prop !== undefined;
}

/** @internal */
export function mergeProps<U extends HTMLElement, T extends Array<React.HTMLAttributes<U> | undefined>>(...props: T) {
    return mergePropsReactAria(...props.filter(isProp));
}

/** @internal */
export function cloneSingleChild(children: React.ReactNode | React.ReactNode[], props?: Record<string, any>, key?: any) {
    return React.Children.map(children, (child) => {
        // Checking isValidElement is the safe way and avoids a typescript
        // error too.
        if (React.isValidElement(child) && React.Children.only(children)) {
            const childProps = child.props as Record<string, any>;
            if (childProps.class) {
                // make sure we retain classnames of both passed props and child
                props ??= {};
                props.class = clsx(childProps.class, props.class);
                props.style = { ...childProps.style, ...props.style };
            }
            return React.cloneElement(child, { ...props, key });
        }
        return child;
    });
}

/**
 * @internal
 */
export function warnAboutMissingStyles(el?: HTMLElement) {
    if (typeof window !== 'undefined' && typeof process !== 'undefined' && process?.env?.NODE_ENV === 'development') {
        const target = el ?? document.querySelector('.lk-room-container');
        if (target && !getComputedStyle(target).getPropertyValue('--lk-has-imported-styles')) {
            log.warn(
                "It looks like you're not using the `@livekit/components-styles package`. To render the UI with the default styling, please import it in your layout or page."
            );
        }
    }
}

/**
 *
 * @internal
 * used to stringify room options to detect dependency changes for react hooks.
 * Replaces processors with strings.
 */
export function roomOptionsStringifyReplacer(key: string, val: unknown) {
    if (key === 'processor' && val && typeof val === 'object' && 'name' in val) {
        return val.name;
    }
    return val;
}
